import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

// --- IMPORT IMAGES (Adjust path if needed based on your folder structure) ---
import MasterBKG from '../../Common/Images/MasterBKG.png';

import add_n from '../../Common/Images/add_n.gif';
import add_o from '../../Common/Images/add_o.gif';
import add_d from '../../Common/Images/add_d.gif';

import cancel_n from '../../Common/Images/cancel_n.gif';
import cancel_o from '../../Common/Images/cancel_o.gif';
import cancel_d from '../../Common/Images/cancel_d.gif';

import remove_white_n from '../../Common/Images/remove_white_n.png';
import remove_white_o from '../../Common/Images/remove_white_o.png';

// Mapping object for dynamic image lookups
const btnImages: Record<string, string> = {
    'add_n': add_n,
    'add_o': add_o,
    'add_d': add_d,
    'cancel_n': cancel_n,
    'cancel_o': cancel_o,
    'cancel_d': cancel_d,
    'remove_white_n': remove_white_n,
    'remove_white_o': remove_white_o,
    'remove_white_d': remove_white_o // VB code uses _o for mousedown on the remove button
};

// --- Interfaces ---
interface TutorOption {
    name: string;
    login: string;
}

// --- Helper: Legacy Image Button Component ---
const ImageButton: React.FC<{ name: string; onClick?: () => void; style?: React.CSSProperties }> = ({ name, onClick, style }) => {
    const [suffix, setSuffix] = useState('n');
    const imageKey = `${name}_${suffix}`;
    const imageSrc = btnImages[imageKey];

    return (
        <img
            src={imageSrc} alt={name}
            style={{ cursor: 'pointer', verticalAlign: 'middle', ...style }}
            onMouseEnter={() => setSuffix('o')}
            onMouseLeave={() => setSuffix('n')}
            onMouseDown={() => setSuffix('d')}
            onMouseUp={() => setSuffix('o')}
            onClick={onClick} draggable={false}
        />
    );
};

const AddTutor: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    // --- State: New Tutor Form ---
    const [loginId, setLoginId] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [gender, setGender] = useState('M');

    // --- State: Dropdowns ---
    const [currentTutors, setCurrentTutors] = useState<TutorOption[]>([]);
    const [potentialTutors, setPotentialTutors] = useState<TutorOption[]>([]);
    const [removeSelection, setRemoveSelection] = useState('-- Select --');
    const [addSelection, setAddSelection] = useState('-- Select --');

    const [loading, setLoading] = useState(false);

    // --- Effects ---
    useEffect(() => {
        // Match the legacy background color of the body
        document.body.style.backgroundColor = '#214461';
        document.body.style.margin = '2px';
        fetchLists();
    }, []);

    // --- Data Fetching ---
    const fetchLists = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/get_tutor_lists`);
            const json = await res.json();
            setCurrentTutors(json.current_tutors || []);
            setPotentialTutors(json.potential_tutors || []);
            setRemoveSelection('-- Select --');
            setAddSelection('-- Select --');
        } catch (err) {
            console.error("Failed to load tutor lists.", err);
        }
    };

    const handleCheckAvail = async () => {
        if (!loginId.trim()) {
            alert('Please enter a Login.');
            return;
        }

        try {
            console.log(`Checking availability for login: ${loginId}`);

            const res = await fetch(`${API_BASE_URL}api/check_login_availability`, {
                method: 'POST',   // <-- THIS WAS MISSING
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    login: loginId
                })
            });

            const json = await res.json();
            alert(json.message);

        } catch (err) {
            console.error(err);
            alert("Error checking availability.");
        }
    };

    const handleAddNewTutor = async () => {
        if (!loginId.trim()) return alert('Please enter a Login.');
        if (!password.trim()) return alert('Please enter Password');
        if (!firstName.trim()) return alert('Please enter the first name');

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/add_new_tutor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    login: loginId,
                    password: password,
                    fname: firstName,
                    lname: lastName,
                    email: email,
                    gender: gender
                })
            });
            const json = await res.json();
            if (json.error) {
                alert(json.error);
            } else {
                alert(json.message);
                setLoginId(''); setPassword(''); setFirstName(''); setLastName(''); setEmail(''); setGender('M');
                fetchLists();
            }
        } catch (err) {
            alert("Error adding tutor.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleTutor = async (action: 'add' | 'remove') => {
        const selectedLogin = action === 'remove' ? removeSelection : addSelection;
        if (selectedLogin === '-- Select --') {
            alert('Select a Tutor.');
            return;
        }

        if (action === 'remove') {
            if (!window.confirm("Are you sure you want to remove this tutor?")) return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/toggle_tutor_status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ login: selectedLogin, action: action })
            });
            const json = await res.json();
            if (json.error) alert(json.error);
            else {
                alert(json.message);
                fetchLists();
            }
        } catch (err) {
            alert(`Error ${action === 'add' ? 'Adding' : 'Removing'} tutor.`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        window.location.href = "/SysAdmin";
    };

    // --- Styles matching the VB HTML structure ---
    const styles = {
        container: { display: 'flex', justifyContent: 'center', fontFamily: 'Arial, sans-serif', fontSize: '10pt' },

        bgWrapper: {
            backgroundImage: `url(${MasterBKG})`,
            backgroundRepeat: 'no-repeat',
            width: '800px',
            height: '580px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center'
        },

        blankSpacer: { height: '120px', width: '100%' },
        innerContainer: { backgroundColor: 'AliceBlue', width: '90%', padding: '4px' },

        mainTable: { width: '100%', borderCollapse: 'collapse' as const },
        tdLeft: { verticalAlign: 'top' as const, width: '60%', border: '1px solid #003300', padding: '0' },
        tdRight: { verticalAlign: 'top' as const, width: '39%', border: '1px solid #003300', padding: '0' },
        tdSpacer: { width: '1%' },

        headerBlue: { backgroundColor: 'blue', color: 'white', fontWeight: 'bold', fontSize: '10pt', padding: '4px', borderRight: '1px solid black', borderLeft: '1px solid black' },

        innerTable: { width: '100%', borderCollapse: 'collapse' as const },
        labelTd: { textAlign: 'right' as const, padding: '4px' },
        inputTd: { textAlign: 'left' as const, padding: '4px', color: 'blue', fontWeight: 'bold' },

        input: { width: '200px', border: '1px solid #003300', padding: '2px', color: 'blue' },
        dropdown: { width: '100px', border: '1px solid #7f9db9', padding: '2px', marginBottom: '5px' },

        btnCheck: { marginLeft: '5px', width: '60px', cursor: 'pointer' },

        bottomActionRow: { display: 'flex', justifyContent: 'center', gap: '20px', padding: '10px 0',width:"60%" }
    };

    return (
        <div style={styles.container}>
            {loading && <Loading />}

            <div style={styles.bgWrapper}>
                <div style={styles.blankSpacer}></div>

                <div style={styles.innerContainer}>
                    <table style={styles.mainTable}>
                        <tbody>
                            <tr>
                                <td style={styles.headerBlue}>Add New Tutor (For Kinetic Education Tutors only)</td>
                                <td style={styles.tdSpacer}></td>
                                <td style={styles.headerBlue}>Remove Existing Tutor</td>
                            </tr>
                            <tr>
                                {/* LEFT COLUMN: Add New Tutor */}
                                <td style={styles.tdLeft}>
                                    <table style={styles.innerTable}>
                                        <tbody>
                                            <tr>
                                                <td style={styles.labelTd}>Login:&nbsp;</td>
                                                <td style={styles.inputTd}>
                                                    <input style={styles.input} maxLength={50} value={loginId} onChange={(e) => setLoginId(e.target.value)} />
                                                    <button style={styles.btnCheck} onClick={handleCheckAvail}>Check</button>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={styles.labelTd}>Password:&nbsp;</td>
                                                <td style={styles.inputTd}><input type="password" style={styles.input} maxLength={50} value={password} onChange={(e) => setPassword(e.target.value)} /></td>
                                            </tr>
                                            <tr>
                                                <td style={styles.labelTd}>First Name:&nbsp;</td>
                                                <td style={styles.inputTd}><input style={styles.input} maxLength={30} value={firstName} onChange={(e) => setFirstName(e.target.value)} /></td>
                                            </tr>
                                            <tr>
                                                <td style={styles.labelTd}>Last Name:&nbsp;</td>
                                                <td style={styles.inputTd}><input style={styles.input} maxLength={30} value={lastName} onChange={(e) => setLastName(e.target.value)} /></td>
                                            </tr>
                                            <tr>
                                                <td style={styles.labelTd}>Gender:&nbsp;</td>
                                                <td style={styles.inputTd}>
                                                    <label style={{ marginRight: '15px', cursor: 'pointer', fontWeight: 'normal' }}>
                                                        <input type="radio" name="gender" checked={gender === 'M'} onChange={() => setGender('M')} style={{ verticalAlign: 'middle' }} /> Male
                                                    </label>
                                                    <label style={{ cursor: 'pointer', fontWeight: 'normal' }}>
                                                        <input type="radio" name="gender" checked={gender === 'F'} onChange={() => setGender('F')} style={{ verticalAlign: 'middle' }} /> Female
                                                    </label>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={styles.labelTd}>eMail ID:&nbsp;</td>
                                                <td style={styles.inputTd}><input style={styles.input} maxLength={200} value={email} onChange={(e) => setEmail(e.target.value)} /></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>

                                <td style={styles.tdSpacer}></td>

                                {/* RIGHT COLUMN: Remove / Add Existing */}
                                <td style={styles.tdRight}>
                                    <table style={styles.innerTable}>
                                        <tbody>
                                            <tr>
                                                <td style={{ textAlign: 'center', paddingTop: '10px' }}>
                                                    <select style={styles.dropdown} value={removeSelection} onChange={(e) => setRemoveSelection(e.target.value)}>
                                                        <option value="-- Select --">-- Select --</option>
                                                        {currentTutors.map(t => <option key={t.login} value={t.login}>{t.name}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={{ textAlign: 'center', paddingBottom: '10px' }}>
                                                    {/* Using legacy image component for Remove */}
                                                    <ImageButton name="remove_white" onClick={() => handleToggleTutor('remove')} />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={{ ...styles.headerBlue, borderLeft: 'none', borderRight: 'none' }}>Add Existing Tutor</td>
                                            </tr>
                                            <tr>
                                                <td style={{ textAlign: 'center', paddingTop: '10px' }}>
                                                    <select style={styles.dropdown} value={addSelection} onChange={(e) => setAddSelection(e.target.value)}>
                                                        <option value="-- Select --">-- Select --</option>
                                                        {potentialTutors.map(t => <option key={t.login} value={t.login}>{t.name}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={{ textAlign: 'center', paddingBottom: '10px' }}>
                                                    {/* Using legacy image component for Add */}
                                                    <ImageButton name="add" onClick={() => handleToggleTutor('add')} />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>

                            {/* Bottom Row Actions */}
                            <tr>
                                <td colSpan={3}>
                                    <div style={styles.bottomActionRow}>
                                        <ImageButton name="add" onClick={handleAddNewTutor} />
                                        <ImageButton name="cancel" onClick={handleCancel} />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AddTutor;